<template>
  <layout>
    <template v-slot:header>
      <global-navigation></global-navigation>
    </template>

    <main v-if="product" class="Product">
      <div class="product-wrapper">
        <div class="product-image">
          <flickity v-if="product.images.nodes.length > 1" ref="flickity" :options="flickityOptions">
            <figure v-for="image in product.images.nodes" :key="image.url">
              <img :src="image.url" :alt="image.altText" />
            </figure>
          </flickity>
          <figure v-else-if="coverImage">
            <img :src="coverImage.url" :alt="coverImage.altText" />
          </figure>
        </div>

        <div class="product-details">
          <div class="product-header">
            <h1>{{ product.title }}</h1>

            <div class="product-price">
              <div v-if="selectedVariant.availableForSale">
                <span v-if="selectedVariant.compareAtPriceV2" class="compare-at-price">
                  ${{ formatPrice(selectedVariant.compareAtPriceV2.amount) }}</span
                >
                <span :class="selectedVariant.compareAtPriceV2 ? 'has-compare-at-price' : ''">
                  ${{ formatPrice(selectedVariant.priceV2.amount) }}
                </span>
              </div>
              <div v-else>
                Sold Out
              </div>
            </div>
          </div>

          <div v-if="shouldDisplayOptions" class="variant-selector-wrapper">
            <select
              v-for="option in product.options"
              @change="(e) => selectOption(option, e.target.value)"
              class="mp-select variant-selector"
              :key="option.name"
            >
              <option v-for="value in option.optionValues" :value="value.name" :key="value.name">{{
                value.name
              }}</option>
            </select>
          </div>

          <button
            v-if="extractSizeChart(product.descriptionHtml) && selectedVariant.availableForSale"
            @click="showSizeGuide = !showSizeGuide"
            class="mp-mini-button size-toggle"
          >
            Sizing Guide {{ showSizeGuide ? '-' : '+' }}
          </button>

          <div
            v-if="extractSizeChart(product.descriptionHtml) && showSizeGuide"
            class="size-chart"
            v-html="extractSizeChart(product.descriptionHtml)"
          ></div>

          <button
            @click="selectedVariant.availableForSale ? addToCart(selectedVariant) : null"
            :class="['mp-button', 'add-to-cart', { highlight: highlightCartButton }]"
            :disabled="!selectedVariant.availableForSale || numberInCart >= 5"
            v-html="buttonText()"
          ></button>

          <div class="back-to-wares nord uppercase text-center">
            <vue-link to="/shop#products">← Back to Shop</vue-link>
          </div>

          <rich-text class="description" :content="removeSizeChart(product.descriptionHtml)" />
        </div>
      </div>

      <product-gallery v-if="collectionId" :collection-id="collectionId" :exclude-by-handle="[$route.params.handle]" />
    </main>

    <template v-slot:footer>
      <global-footer :border="hasBorder"></global-footer>
    </template>
  </layout>
</template>

<script>
import { getProduct, addLineItems } from '@/lib/shopify-utils'
import Flickity from 'vue-flickity'
import MailchimpSubscribe from 'vue-mailchimp-subscribe'
import RichText from '@/components/RichText'
import PageMixin from '@/mixins/PageMixin'
import ProductGallery from '@/components/ProductGallery'

export default {
  name: 'Page',
  mixins: [PageMixin],
  components: { Flickity, RichText, MailchimpSubscribe, ProductGallery },
  data() {
    return {
      showSizeGuide: false,
      flickityOptions: {
        arrowShape: {
          x0: 10,
          x1: 60,
          y1: 50,
          x2: 68,
          y2: 44,
          x3: 26,
        },
      },
      product: null,
      selectedVariant: null,
      selectedOptions: [],
      cartTextOverride: null,
      highlightCartButton: false,
    }
  },
  computed: {
    console: () => console,
    hasBorder() {
      return this.hasPage && (!!this.page.videofile || !!(this.page.featuredimage && this.page.featuredimage.large))
    },
    collectionId() {
      const site = this.$store.state.content['/*'] || {}

      return site.collectionId
    },
    mailchimpurl() {
      return this.site.mailchimpurl
    },
    mailchimpid() {
      return this.site.mailchimpid
    },
    mailchimplist() {
      return this.site.mailchimplist
    },
    showNewsletter() {
      return this.mailchimpurl && this.mailchimpid && this.mailchimplist
    },
    shouldDisplayOptions() {
      return (
        this.product.options.length > 1 ||
        (this.product.options.length == 1 && this.product.options[0].optionValues.length > 1)
      )
    },
    coverImage() {
      return this.product.featuredImage
    },
    numberInCart() {
      // check if this product is already in the cart
      if (this.$store.state.cartData) {
        const lineItems = this.$store.state.cartData.lines.edges
        let productInCart = null

        if (lineItems.length > 0) {
          productInCart = lineItems.find((item) => item.node.merchandise.id === this.selectedVariant.id)
        }

        return productInCart ? productInCart.node.quantity : 0
      }

      return 0
    },
  },
  methods: {
    removeSizeChart(descriptionHtml) {
      // Removes all table elements from Shopify description
      var doc = new DOMParser().parseFromString(descriptionHtml, 'text/html')
      var tables = [...doc.querySelectorAll('table')]
      tables.forEach((table) => table.remove())
      return doc.body.innerHTML
    },
    extractSizeChart(descriptionHtml) {
      // Extracts first table element from Shopify description
      var doc = new DOMParser().parseFromString(descriptionHtml, 'text/html')
      var sizeChart = doc.querySelector('table')
      return sizeChart ? sizeChart.outerHTML : false
    },
    formatPrice(price) {
      const formatted = parseFloat(parseFloat(price).toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return formatted.replace('.00', '')
    },
    buttonText() {
      if (this.selectedVariant.availableForSale) {
        if (this.cartTextOverride) {
          return this.cartTextOverride
        } else if (this.numberInCart >= 5) {
          return '5 item maximum'
        } else {
          return 'Add to cart'
        }
      } else {
        return 'Sold Out'
      }
    },
    async addToCart(variant) {
      this.cartTextOverride = 'Adding to cart…'

      const newLinesArray = [
        {
          merchandiseId: variant.id,
          quantity: 1,
        },
      ]

      const response = await addLineItems({
        cartId: this.$store.state.cartData.id,
        lines: newLinesArray,
      })

      if (response.errors) {
        console.error('Error adding line item:', response.errors)
        return
      }

      this.$store.dispatch('setCartData', response.data.cartLinesAdd.cart)

      this.cartTextOverride = 'Added to cart!'
      this.highlightCartButton = true

      setTimeout(() => {
        this.cartTextOverride = null
        this.highlightCartButton = false
      }, 2000)
    },
    selectOption(option, value) {
      const newSelectedOptions = this.selectedOptions.map((selectedOption) => {
        if (selectedOption.name === option.name) {
          return { ...selectedOption, value }
        }
        return selectedOption
      })

      // now we need to get the variant that matches the new selected options
      const variant = this.product.variants.nodes.find((variant) => {
        return variant.selectedOptions.every((selectedOption) => {
          return newSelectedOptions.some(
            (option) => option.name === selectedOption.name && option.value === selectedOption.value
          )
        })
      })

      this.selectedVariant = variant
    },
  },
  async mounted() {
    const product = await getProduct({ handle: this.$route.params.handle })

    if (product.data) {
      this.product = product.data.product
      this.selectedVariant = this.product.selectedOrFirstAvailableVariant
      this.selectedOptions = this.selectedVariant.selectedOptions
    }
  },
}
</script>

<style scoped>
.back-to-wares {
  grid-column: 1 / -1;
  font-size: 1.15rem;
}

.product-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding-left: 2rem;
  padding-right: 2rem;
  padding-top: 4rem;
  grid-gap: 8rem 2rem;
}

.product-image {
  border: 1px dashed #fff;
  border-radius: var(--br);
  padding: 2rem 2rem 4.5rem 2rem;
  align-self: flex-start;
  align-self: start;
}

.product-image >>> figure {
  width: 100%;
  padding-top: 90%;
  position: relative;
}

.product-image >>> figure > * {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.product-image >>> .flickity-button {
  background: none;
  border-radius: 0;
}

.product-image >>> .flickity-button:focus {
  box-shadow: none;
}

.product-image >>> .flickity-prev-next-button {
  /*width: 2.25rem;
  height: 2.25rem;*/
  width: 3rem;
  height: 3rem;
}

@media (max-width: 1000px) {
  .product-image >>> .flickity-prev-next-button.previous {
    left: -5px;
  }

  .product-image >>> .flickity-prev-next-button.next {
    right: -5px;
  }
}

.product-image >>> .flickity-button-icon {
  /*fill: #fa3b41;*/
  fill: #fff;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.product-image >>> .flickity-page-dots {
  bottom: -3.125rem;
}

.product-image >>> .flickity-page-dots .dot {
  opacity: 1;
  border: 1px solid #fff;
  background: none;
  margin: 0 6px;
  width: 12px;
  height: 12px;
}

.product-image >>> .flickity-page-dots .dot.is-selected {
  background: #fff;
}

.product-details {
  padding-left: 8%;
  padding-right: 8%;
}

.product-details > * + * {
  margin-top: 3rem;
}

.variant-selector-wrapper {
  width: 60%;
  display: inline-block;
}

.variant-selector {
  width: 100%;
}

.size-toggle {
  width: calc(40% - 2rem);
  margin-left: 2rem;
}

.add-to-cart {
  width: 100%;
}

.add-to-cart.highlight {
  color: #fa3b41;
}

.adding-to-cart {
  cursor: wait !important;
}

.product-header {
  text-align: center;
  font: var(--nord);
  font-size: 1.7rem;
  border-bottom: 1px dashed #fff;
}

.product-header h1 {
  font-size: 3.4rem;
  text-transform: uppercase;
}

.product-price {
  margin-top: 1rem;
  margin-bottom: 2.5rem;
}

.compare-at-price {
  position: relative;
}

.compare-at-price::after {
  content: '';
  width: 100%;
  height: 2px;
  background: #fff;
  display: block;
  position: absolute;
  top: 50%;
  left: 0;
}

.has-compare-at-price {
  color: #fa3b41;
}

.description >>> table {
  display: none;
}

.size-chart:empty {
  display: none;
}

.size-chart {
  font: var(--nord);
  font-size: 1.25rem;
}

.size-chart >>> table {
  border-collapse: collapse;
}

.size-chart >>> td {
  border: 1px solid #ccc;
  text-align: center;
  padding: 1.15rem 0.5rem;
}

.size-chart >>> tr:first-child td:first-child {
  border: none;
}

.related-products {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  padding-left: 2rem;
  padding-right: 2rem;
  padding-top: 8rem;
  grid-gap: 2rem;
}

.related-grid-item {
  border: 1px dashed #fff;
  border-radius: var(--br);
  padding: 3rem;
  text-align: center;
}

.related-grid-item.empty-item {
  padding-top: 6rem;
  padding-bottom: 6rem;
}

.related-grid-item figure {
  padding-top: 60%;
  position: relative;
  margin-bottom: 2rem;
}

.related-grid-item figure > * {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/**
 * Newsletter
 */

.mailchimp-form {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 1rem;
  padding-top: 2.5rem;
  padding-bottom: 3.2rem;
  border-bottom: 1px dashed white;
}

.mailchimp-form input[type='email'] {
  grid-column: span 3 / span 3;
}

.mailchimp-form button {
  grid-column: span 2 / span 2;
}

.mailchimp-form .message {
  grid-column: 1 / -1;
}

@media (max-width: 1000px) {
  .mailchimp-form input[type='email'],
  .mailchimp-form button {
    grid-column: 1 / -1;
  }
}

/**
 *
 */

.not-found {
  text-align: center;
  font: var(--nord);
  text-transform: uppercase;
}

.content {
  padding: 4rem 19% 6rem 19%;
}

@media (max-width: 1360px) {
  .content {
    padding: 4rem 20%;
  }
}

@media (max-width: 1000px) {
  .content {
    padding: 4rem 2rem;
  }
  .related-products {
    grid-template-columns: 1fr;
  }
  .product-details {
    padding-left: 0;
    padding-right: 0;
  }
  .product-wrapper {
    grid-template-columns: 1fr;
    padding-top: 2rem;
    row-gap: 6rem;
  }
}
</style>
